import { useEffect, useRef, useState } from "react";
import { Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const QUICK = ["Add more job ad keywords to my bullet points", "Make my summary shorter and punchier", "Make it sound more senior", "Put my most relevant skills first"];

export default function AskForChange({ app, onUpdate }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const run = async (instruction = text) => {
    if (instruction.trim().length < 2 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/applications/${app.id}/rewrite`, { instruction: instruction.trim() });
      const started = Date.now();
      while (alive.current && Date.now() - started < 150000) {
        await sleep(2000);
        const { data: job } = await api.get(`/cv/import/${data.id}`);
        if (job.status === "done") {
          const { data: fresh } = await api.get(`/applications/${app.id}`);
          onUpdate({ tailored_cv: fresh.tailored_cv });
          setText("");
          toast.success("Done. Your CV has been rewritten.");
          return;
        }
        if (job.status === "failed") throw new Error("failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't rewrite it this time. Please try again.");
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return (
    <div className="gi-card p-5 sm:p-6" data-testid="ask-for-change">
      <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2"><Wand2 className="w-5 h-5 text-brand" aria-hidden="true" /> Ask for a change</h3>
      <p className="text-[15px] text-body mt-1">Tell us what to change and we'll rewrite your CV straight away.</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <label htmlFor="ask-change-input" className="sr-only">What should we change?</label>
        <input id="ask-change-input" data-testid="ask-change-input" value={text} maxLength={600} disabled={busy}
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder='e.g. "Add Power BI" or "Make my summary shorter"' className="gi-input flex-1" />
        <button data-testid="ask-change-submit" onClick={() => run()} disabled={busy || text.trim().length < 2} className="gi-btn gi-btn-primary shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" aria-hidden="true" />} {busy ? "Rewriting…" : "Rewrite my CV"}
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {QUICK.map((q, i) => (
          <button key={q} data-testid={`ask-change-quick-${i}`} onClick={() => run(q)} disabled={busy}
            className="px-3 py-1.5 min-h-[36px] rounded-full border border-line bg-surface text-sm text-ink hover:border-brand/40 hover:bg-brand-soft transition-colors disabled:opacity-50">{q}</button>
        ))}
      </div>
      {busy && <p className="mt-3 text-sm text-faint" data-testid="ask-change-progress">Rewriting your CV. This usually takes 20 to 40 seconds.</p>}
    </div>
  );
}
